import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import { percentFormatter } from '../../util/formatHelper';
import { STATISTIC_TYPE_SELF_SERVICE } from '../../constants/statisticTypes';
import {
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipContentStyle,
  getRechartsTooltipLabelStyle,
  getStatisticsChartCursorFill,
  getStatisticsKpiHelperColor,
  getStatisticsKpiLabelColor,
  getStatisticsSurfaceBackground,
  getStatisticsTableRowBorderTop,
  getRechartsLegendBarProps,
  getRechartsLegendPieProps,
  getStatisticsHtmlThStyle,
  getStatisticsPageRootStyle,
} from '../../util/chartTheme';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// History tracking started on 2026-01-20, so this is the earliest valid date for statistics
const HISTORY_TRACKING_START_DATE = dayjs('2026-01-20');

const getDefaultStartDate = () => {
  const monthStart = dayjs().startOf('month');
  return monthStart.isBefore(HISTORY_TRACKING_START_DATE)
    ? HISTORY_TRACKING_START_DATE.format('YYYY-MM-DD')
    : monthStart.format('YYYY-MM-DD');
};

const defaultStartDate = getDefaultStartDate();
const defaultEndDate = dayjs().endOf('month').format('YYYY-MM-DD');

const ACTION_TYPE_LABELS = {
  addParticipants: 'Teilnehmer hinzufügen',
  cancelParticipants: 'Teilnehmer stornieren',
  shiftBooking: 'Buchung verschieben',
  shiftResource: 'Ressource wechseln',
  setPackage: 'Paket ändern',
  updateUpsell: 'Upsell ändern',
  cancelBooking: 'Stornierungen',
};

const SelfServiceStatistics = () => {
  const theme = useTheme();
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const chartTooltipContentStyle = useMemo(() => getRechartsTooltipContentStyle(theme), [theme]);
  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const chartCursorFill = useMemo(() => getStatisticsChartCursorFill(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const chartLegendPieProps = useMemo(() => getRechartsLegendPieProps(theme), [theme]);
  const htmlThStyle = useCallback((extra = {}) => getStatisticsHtmlThStyle(theme, extra), [theme]);
  const tooltipLabelStyle = useMemo(() => getRechartsTooltipLabelStyle(theme), [theme]);
  const tableRowBorderTop = useMemo(() => getStatisticsTableRowBorderTop(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);
  const statusColors = useMemo(
    () => ({
      pending: theme.palette.warning.main,
      confirmed: theme.palette.success.main,
      processing: theme.palette.primary.main,
      expired: theme.palette.grey[500],
      rejected: theme.palette.error.main,
      canceled: theme.palette.grey[700],
    }),
    [theme]
  );
  const typeColors = useMemo(
    () => [
      theme.palette.success.main,
      theme.palette.primary.main,
      theme.palette.warning.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.grey[700],
      theme.palette.grey[600],
    ],
    [theme]
  );
  const lineSeriesColors = useMemo(
    () => ({
      warn: theme.palette.warning.main,
      success: theme.palette.success.main,
      primary: theme.palette.primary.main,
    }),
    [theme]
  );

  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  });
  const [chartType, setChartType] = useState('line');
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  const filterParams = useMemo(
    () => ({
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branchScope: 'all',
    }),
    [appliedFilter]
  );

  const {
    payload: stats,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status,
    job,
    error,
    loadCacheEntry,
    refresh: refreshStatistic,
  } = useStatisticJob({
    statisticType: STATISTIC_TYPE_SELF_SERVICE,
    endpoint: '/api/v1/statistic/selfService',
    filterParams,
  });

  const { entries: cacheEntries, isLoading: entriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_SELF_SERVICE,
  });

  useEffect(() => {
    if (status === 'failed' && error) {
      toast.error(error);
    }
  }, [status, error]);

  const handleSelectEntry = useCallback(
    entryId => {
      setSelectedEntryId(entryId);
      if (entryId) loadCacheEntry(entryId);
    },
    [loadCacheEntry]
  );

  const isFetching = status === 'fetching' || status === 'pending' || status === 'running';

  const applyFilters = useCallback(
    (nextStart, nextEnd) => {
      const s = nextStart || startDate;
      const e = nextEnd || endDate;
      if (!s || !e || !s.isValid() || !e.isValid()) {
        toast.error('Bitte wähle gültige Daten aus');
        return;
      }
      if (s.isAfter(e)) {
        toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
        return;
      }
      if (s.isBefore(HISTORY_TRACKING_START_DATE)) {
        toast.error('Das Startdatum darf nicht vor dem 20.01.2026 liegen (Start der History-Aufzeichnung)');
        return;
      }
      setSelectedEntryId(null);
      setAppliedFilter({
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
      });
    },
    [startDate, endDate]
  );

  const cards = useMemo(() => {
    if (!stats?.percentages || !stats?.counts || !stats?.actions) return [];
    const totalBookings = Number(stats.counts.totalBookings || 0);
    const usedBookings = Number(stats.counts.selfServiceUsedBookings || 0);
    const changedBookings = Number(stats.counts.changedBookings || 0);
    const ssChangedBookings = Number(stats.counts.selfServiceChangedBookings || 0);

    const totalActions = Number(stats.actions.total || 0);
    const pendingActions = Number(stats.actions.pending || 0);
    const confirmedActions = Number(stats.actions.confirmed || 0);

    const confirmRate = totalActions > 0 ? (confirmedActions / totalActions) * 100 : 0;

    return [
      {
        label: 'Self Service Nutzung',
        value: `${percentFormatter.format(Number(stats.percentages.selfServiceUsagePercent || 0))} %`,
        helper: `${usedBookings} von ${totalBookings} Buchungen`,
      },
      {
        label: 'Self Service Änderungen (von allen Änderungen)',
        value: `${percentFormatter.format(Number(stats.percentages.selfServiceChangePercent || 0))} %`,
        helper: `${ssChangedBookings} von ${changedBookings} geänderten Buchungen`,
      },
      {
        label: 'Änderungen angefordert (pending)',
        value: pendingActions,
        helper: 'Anzahl offener Self‑Service Anfragen',
      },
      {
        label: 'Änderungen bestätigt/ausgeführt',
        value: confirmedActions,
        helper: `${percentFormatter.format(confirmRate)} % der Anfragen`,
      },
      {
        label: 'Bestätigungsquote',
        value: `${percentFormatter.format(confirmRate)} %`,
        helper: `${confirmedActions} von ${totalActions} Anfragen`,
      },
    ];
  }, [stats]);

  const chartData = useMemo(() => {
    const daily = stats?.timeSeries?.daily || [];
    return daily.map(p => {
      const date = dayjs(p.date);
      return {
        ...p,
        label: date.isValid() ? date.format('DD.MM.') : String(p.date || ''),
        actionsPending: Number(p.actionCounts?.pending || 0),
        actionsConfirmed:
          Number(p.actionCounts?.confirmed || 0) + Number(p.actionCounts?.processing || 0),
        actionsIgnored:
          Number(p.actionCounts?.expired || 0) +
          Number(p.actionCounts?.rejected || 0) +
          Number(p.actionCounts?.canceled || 0),
      };
    });
  }, [stats]);

  const statusPieData = useMemo(() => {
    const byStatus = stats?.actions?.byStatus || {};
    const entries = Object.entries(byStatus)
      .map(([name, value]) => ({ name, value: Number(value || 0) }))
      .filter(e => e.value > 0)
      .sort((a, b) => b.value - a.value);
    return entries;
  }, [stats]);

  const actionTypeRows = useMemo(() => {
    const rows = stats?.actions?.byType || [];
    return rows.map(r => ({
      ...r,
      label: ACTION_TYPE_LABELS[r.type] || r.type,
    }));
  }, [stats]);

  const actionTypePieData = useMemo(() => {
    return actionTypeRows
      .map(r => ({ name: r.label, value: Number(r.total || 0) }))
      .filter(r => r.value > 0)
      .slice(0, 12);
  }, [actionTypeRows]);

  return (
    <div style={getStatisticsPageRootStyle(theme)}>
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      startDatePickerProps={{
        minDate: HISTORY_TRACKING_START_DATE,
        slotProps: {
          textField: {
            fullWidth: true,
            error: startDate?.isBefore(HISTORY_TRACKING_START_DATE),
            helperText: startDate?.isBefore(HISTORY_TRACKING_START_DATE)
              ? 'Beginn der Aufzeichnungen'
              : undefined,
          },
        },
      }}
      endDatePickerProps={{ minDate: HISTORY_TRACKING_START_DATE }}
      onApply={applyFilters}
      applyDisabled={isFetching}
      applyExtraActions={
        <Button
          variant='outlined'
          onClick={() => setChartType(prev => (prev === 'line' ? 'bar' : 'line'))}
          sx={{ minWidth: 56, minHeight: 56, padding: 0, borderRadius: 2 }}
          title={chartType === 'line' ? 'Balken' : 'Linie'}
        >
          {chartType === 'line' ? '▦' : '〰'}
        </Button>
      }
      status={status}
      isStale={isStale}
      cacheComputedAt={cacheComputedAt}
      cacheExpiresAt={cacheExpiresAt}
      job={job}
      error={error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedEntryId}
      onSelectEntry={handleSelectEntry}
      entriesLoading={entriesLoading}
      onRefresh={refreshStatistic}
      hasPayload={!!stats}
    >
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 1 }}>
          Kennzahlen
        </Typography>
        {stats && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
            }}
          >
            {cards.map(card => (
              <article
                key={card.label}
                style={{
                  background: statSurfaceBg,
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                }}
              >
                <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{card.label}</span>
                <strong style={{ fontSize: '1.5rem' }}>{card.value}</strong>
                <small style={{ color: kpiHelperColor }}>{card.helper}</small>
              </article>
            ))}
          </div>
        )}
      </section>

      {stats && (
        <section>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent='space-between'
            sx={{ mb: 1 }}
          >
            <Typography variant='h6' component='h2' sx={{ m: 0 }}>
              Self Service Verlauf
            </Typography>
          </Stack>
          <Paper elevation={3} sx={{ p: 2, background: statSurfaceBg, height: 360 }}>
            <ResponsiveContainer width='100%' height='100%'>
              {chartType === 'line' ? (
                <LineChart data={chartData}>
                  <CartesianGrid stroke={chartGridStroke} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis stroke={axisStroke} allowDecimals={false} />
                  <Tooltip
                    contentStyle={chartTooltipContentStyle}
                    labelStyle={tooltipLabelStyle}
                  />
                  <Legend {...chartLegendBarProps} />
                  <Line
                    type='monotone'
                    dataKey='selfServiceUsedBookings'
                    name='Buchungen mit Self‑Service Nutzung'
                    stroke={lineSeriesColors.warn}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type='monotone'
                    dataKey='selfServiceChangedBookings'
                    name='Buchungen mit bestätigter Änderung'
                    stroke={lineSeriesColors.success}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type='monotone'
                    dataKey='actionsPending'
                    name='Anfragen (pending)'
                    stroke={lineSeriesColors.primary}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              ) : (
                <BarChart data={chartData}>
                  <CartesianGrid stroke={chartGridStroke} vertical={false} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis stroke={axisStroke} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: chartCursorFill }}
                    contentStyle={chartTooltipContentStyle}
                    labelStyle={tooltipLabelStyle}
                  />
                  <Legend {...chartLegendBarProps} />
                  <Bar
                    dataKey='selfServiceUsedBookings'
                    name='Nutzung'
                    fill={lineSeriesColors.warn}
                    stackId='a'
                  />
                  <Bar
                    dataKey='selfServiceChangedBookings'
                    name='Bestätigt'
                    fill={lineSeriesColors.success}
                    stackId='b'
                  />
                  <Bar dataKey='actionsPending' name='Pending' fill={lineSeriesColors.primary} stackId='c' />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </section>
      )}

      {stats && (statusPieData.length > 0 || actionTypePieData.length > 0) && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Self‑Service Verteilung
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              alignItems: 'stretch',
            }}
          >
            {statusPieData.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  background: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant='subtitle1' component='h3' sx={{ mt: 0, mb: 1.5, fontWeight: 600 }}>
                  Status der Self‑Service Anfragen
                </Typography>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey='value'
                        stroke='none'
                      >
                        {statusPieData.map(entry => (
                          <Cell
                            key={entry.name}
                            fill={statusColors[entry.name] || theme.palette.grey[600]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={value => String(value)}
                        contentStyle={chartTooltipContentStyle}
                      />
                      <Legend {...chartLegendPieProps} verticalAlign='bottom' height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Paper>
            )}

            {actionTypePieData.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  background: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant='subtitle1' component='h3' sx={{ mt: 0, mb: 1.5, fontWeight: 600 }}>
                  Anfragen nach Änderungstyp
                </Typography>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={actionTypePieData}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey='value'
                        stroke='none'
                      >
                        {actionTypePieData.map((entry, idx) => (
                          <Cell
                            key={`${entry.name}-${idx}`}
                            fill={typeColors[idx % typeColors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={value => String(value)}
                        contentStyle={chartTooltipContentStyle}
                      />
                      <Legend {...chartLegendPieProps} verticalAlign='bottom' height={48} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Paper>
            )}
          </Box>
        </section>
      )}

      {stats && actionTypeRows.length > 0 && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Änderungstypen
          </Typography>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              background: statSurfaceBg,
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '640px',
                }}
              >
                <thead>
                  <tr>
                    <th style={htmlThStyle()}>Typ</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Anfragen</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Pending</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Bestätigt</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Bestätigungsquote</th>
                  </tr>
                </thead>
                <tbody>
                  {actionTypeRows.map(row => (
                    <tr key={row.type}>
                      <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                        {row.label}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {row.total}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {row.pending}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {row.confirmed}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {percentFormatter.format(Number(row.confirmRate || 0))} %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Paper>
        </section>
      )}
    </StatisticViewShell>
    </div>
  );
};

export default SelfServiceStatistics;
